import React, { useState, useMemo } from 'react';
import { formatCurrency } from '../../utils/formatting';

/**
 * カードの締め日から対象期間を算出
 */
function getCardBillingPeriod(card, targetYear, targetMonth) {
    const { closingDay, paymentDay, paymentMonthOffset } = card;

    const prevMonth = targetMonth === 1 ? 12 : targetMonth - 1;
    const prevYear = targetMonth === 1 ? targetYear - 1 : targetYear;

    const getActualDay = (year, month, day) => {
        const lastDay = new Date(year, month, 0).getDate();
        return Math.min(day, lastDay);
    };

    const closingDayActual = getActualDay(targetYear, targetMonth, closingDay);
    const prevClosingDayActual = getActualDay(prevYear, prevMonth, closingDay);

    const startDay = prevClosingDayActual + 1;
    const lastDayOfPrevMonth = new Date(prevYear, prevMonth, 0).getDate();

    let startY = prevYear, startM = prevMonth, startD = startDay;
    if (startDay > lastDayOfPrevMonth) {
        // 翌月1日に繰り上げ
        startM = prevMonth + 1;
        startD = 1;
        if (startM > 12) { startM = 1; startY += 1; }
    }

    const start = `${startY}-${String(startM).padStart(2, '0')}-${String(startD).padStart(2, '0')}`;
    const end = `${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(closingDayActual).padStart(2, '0')}`;

    let payYear = targetYear;
    let payMonth = targetMonth + paymentMonthOffset;
    if (payMonth > 12) { payMonth -= 12; payYear += 1; }
    const payDayActual = getActualDay(payYear, payMonth, paymentDay);
    const paymentDate = `${payYear}-${String(payMonth).padStart(2, '0')}-${String(payDayActual).padStart(2, '0')}`;

    return { start, end, paymentDate };
}

/**
 * 指定カード・指定締め期間の未払金合計を算出
 */
function getCardPeriodTotal(journals, cardId, startDate, endDate) {
    return journals
        .filter(j => {
            if (j.deletedAt) return false;
            if (j.date < startDate || j.date > endDate) return false;
            return j.lines.some(l => l.type === 'credit' && l.creditCardId === cardId);
        })
        .reduce((sum, j) => {
            const cardLines = j.lines.filter(l => l.type === 'credit' && l.creditCardId === cardId);
            return sum + cardLines.reduce((s, l) => s + l.amount, 0);
        }, 0);
}

/**
 * 消込済みかどうか判定（引落し日付近の借方未払金仕訳を探す）
 */
function findSettlementJournal(journals, card, paymentDate) {
    return journals.find(j =>
        !j.deletedAt &&
        j.date === paymentDate &&
        j.lines.some(l => l.type === 'debit' && l.accountId === card.unpaidAccountId) &&
        j.lines.some(l => l.type === 'credit' && l.accountId === card.paymentAccountId)
    );
}

const CardSettlement = ({ accounts, journals, setJournals, showAlert, currentFiscalYear, fiscalYears, creditCards }) => {
    const [selectedCardId, setSelectedCardId] = useState(creditCards?.[0]?.id || '');
    const [customAmount, setCustomAmount] = useState({});

    const selectedCard = creditCards?.find(c => c.id === selectedCardId);

    // 現在の年度の期間を取得
    const currentFy = fiscalYears?.find(fy => fy.year === currentFiscalYear);
    const endMonth = currentFy?.endMonth || 3;

    // 年度内の月リストを生成
    const months = useMemo(() => {
        if (!selectedCard) return [];
        const result = [];
        const startMonth = endMonth === 12 ? 1 : endMonth + 1;
        const startYear = endMonth === 12 ? currentFiscalYear : currentFiscalYear;

        for (let i = 0; i < 12; i++) {
            let m = startMonth + i;
            let y = startYear;
            if (m > 12) { m -= 12; y += 1; }
            result.push({ year: y, month: m });
        }
        return result;
    }, [currentFiscalYear, endMonth, selectedCard]);

    // 各月の消込情報を計算
    const periodData = useMemo(() => {
        if (!selectedCard || months.length === 0) return [];

        return months.map(({ year, month }) => {
            const period = getCardBillingPeriod(selectedCard, year, month);
            const total = getCardPeriodTotal(journals, selectedCard.id, period.start, period.end);
            const settlement = findSettlementJournal(journals, selectedCard, period.paymentDate);
            const settlementAmount = settlement
                ? settlement.lines.filter(l => l.type === 'debit' && l.accountId === selectedCard.unpaidAccountId)
                    .reduce((s, l) => s + l.amount, 0)
                : 0;

            return {
                year, month,
                ...period,
                total,
                isSettled: !!settlement,
                settlementAmount,
                difference: total - settlementAmount
            };
        });
    }, [selectedCard, months, journals]);

    // カード残高サマリー
    const balanceSummary = useMemo(() => {
        if (!selectedCard) return null;

        // 全creditCardId付き未払金（貸方）の合計
        const totalUnpaid = journals
            .filter(j => !j.deletedAt)
            .flatMap(j => j.lines)
            .filter(l => l.type === 'credit' && l.creditCardId === selectedCard.id)
            .reduce((s, l) => s + l.amount, 0);

        // 消込済み（借方未払金でカード口座が貸方にある仕訳）
        const totalSettled = journals
            .filter(j => !j.deletedAt)
            .filter(j =>
                j.lines.some(l => l.type === 'debit' && l.accountId === selectedCard.unpaidAccountId) &&
                j.lines.some(l => l.type === 'credit' && l.accountId === selectedCard.paymentAccountId)
            )
            .flatMap(j => j.lines)
            .filter(l => l.type === 'debit' && l.accountId === selectedCard.unpaidAccountId)
            .reduce((s, l) => s + l.amount, 0);

        return {
            totalUnpaid,
            totalSettled,
            balance: totalUnpaid - totalSettled + (selectedCard.openingBalance || 0)
        };
    }, [selectedCard, journals]);

    const paymentAccountName = selectedCard ? accounts.find(a => a.id === selectedCard.paymentAccountId)?.name || '未設定' : '';
    const unpaidAccountName = selectedCard ? accounts.find(a => a.id === selectedCard.unpaidAccountId)?.name || '未払金' : '';

    const handleCreateSettlement = (pd) => {
        if (!selectedCard) return;
        const amount = customAmount[`${pd.year}-${pd.month}`] ?? pd.total;
        if (amount <= 0) {
            showAlert('消込金額が0以下です', 'error');
            return;
        }

        const journal = {
            id: `settlement_${selectedCard.id}_${pd.year}${String(pd.month).padStart(2, '0')}_${Date.now()}`,
            date: pd.paymentDate,
            description: `${selectedCard.name} ${pd.month}月分引落し`,
            lines: [
                { type: 'debit', accountId: selectedCard.unpaidAccountId, amount, taxType: 'non_taxable', taxRate: 0 },
                { type: 'credit', accountId: selectedCard.paymentAccountId, amount, taxType: 'non_taxable', taxRate: 0 }
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        setJournals(prev => [...prev, journal]);
        showAlert(`${selectedCard.name} ${pd.month}月分の消込仕訳を作成しました`);
    };

    if (!creditCards || creditCards.length === 0) {
        return (
            <div>
                <div className="page-header">
                    <h2>カード消込</h2>
                </div>
                <div className="card">
                    <div className="empty-state">
                        <h3>クレジットカードが未登録です</h3>
                        <p>「データ管理」→「クレジットカード」タブからカードを登録してください</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <h2>カード消込</h2>
            </div>

            <div className="card">
                <div className="form-row">
                    <div className="form-group">
                        <label>カード</label>
                        <select
                            className="form-control"
                            value={selectedCardId}
                            onChange={e => setSelectedCardId(e.target.value)}
                        >
                            {creditCards.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {selectedCard && (
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '13px', color: '#666' }}>
                        <span>締め日: {selectedCard.closingDay === 31 ? '末日' : `${selectedCard.closingDay}日`}</span>
                        <span>引落し: {selectedCard.paymentMonthOffset === 1 ? '翌月' : '翌々月'}{selectedCard.paymentDay}日</span>
                        <span>口座: {paymentAccountName}</span>
                        <span>未払金科目: {unpaidAccountName}</span>
                    </div>
                )}
            </div>

            {/* 残高サマリー */}
            {balanceSummary && (
                <div className="card" style={{ background: '#f8f9fa' }}>
                    <h3 className="card-title">カード残高サマリー</h3>
                    <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        <div className="stat-card">
                            <h3>利用総額</h3>
                            <div className="value">¥{formatCurrency(balanceSummary.totalUnpaid)}</div>
                        </div>
                        <div className="stat-card">
                            <h3>消込済み</h3>
                            <div className="value">¥{formatCurrency(balanceSummary.totalSettled)}</div>
                        </div>
                        <div className="stat-card">
                            <h3>未払残高</h3>
                            <div className="value" style={{ color: balanceSummary.balance > 0 ? '#e67e22' : '#27ae60' }}>
                                ¥{formatCurrency(balanceSummary.balance)}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 月別消込一覧 */}
            {periodData.length > 0 && (
                <div className="card">
                    <h3 className="card-title">月別消込状況</h3>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>対象月</th>
                                <th>締め期間</th>
                                <th>引落し日</th>
                                <th className="text-right">未払金合計</th>
                                <th className="text-right">引落し額</th>
                                <th>状態</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {periodData.map(pd => {
                                const key = `${pd.year}-${pd.month}`;
                                const inputAmount = customAmount[key] ?? '';
                                return (
                                    <tr key={key}>
                                        <td>{pd.year}/{pd.month}月</td>
                                        <td style={{ fontSize: '12px' }}>{pd.start} 〜 {pd.end}</td>
                                        <td>{pd.paymentDate}</td>
                                        <td className="text-right amount">
                                            {pd.total > 0 ? `¥${formatCurrency(pd.total)}` : '-'}
                                        </td>
                                        <td className="text-right">
                                            {pd.isSettled ? (
                                                <span className="amount">¥{formatCurrency(pd.settlementAmount)}</span>
                                            ) : pd.total > 0 ? (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    style={{ width: '120px', textAlign: 'right', display: 'inline-block' }}
                                                    value={inputAmount === '' ? pd.total : inputAmount}
                                                    onChange={e => {
                                                        const v = e.target.value.replace(/[^0-9]/g, '');
                                                        setCustomAmount(prev => ({ ...prev, [key]: v ? Number(v) : '' }));
                                                    }}
                                                />
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {pd.isSettled ? (
                                                <span style={{ color: '#27ae60', fontWeight: 'bold' }}>消込済</span>
                                            ) : pd.total > 0 ? (
                                                <span style={{ color: '#e67e22' }}>未消込</span>
                                            ) : (
                                                <span style={{ color: '#aaa' }}>利用なし</span>
                                            )}
                                        </td>
                                        <td>
                                            {!pd.isSettled && pd.total > 0 && (
                                                <button
                                                    className="btn btn-primary btn-small"
                                                    onClick={() => handleCreateSettlement(pd)}
                                                >
                                                    消込仕訳作成
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CardSettlement;
